// Canvas renderer for the country-level macro view (P2 §5).
//
// Macro rings represent an entire backend's catalogue at zoom ≤ macroMaxZoom.
// They differ from cluster-tier rings in two ways:
//
//   1. Always rendered as rings, never as the cluster-tier single-child dot
//      (a backend with one playground is still a "region", not a singleton).
//   2. Six visual variants — healthy (filled segments + count), offline
//      (dashed outline, muted, "offline" label, last known count), importing
//      (solid blue ring, "updating" label — osm2pgsql actively running),
//      degraded (amber ring, "no data" label — backend reachable but empty),
//      filtered-empty (grey ring, "no match" label — backend healthy but the
//      active filter excludes all of its playgrounds), and can't-filter
//      (full segments + dashed grey halo + "unfiltered" label — backend
//      couldn't apply the filter, so its whole catalogue is shown, marked as
//      such; #688).
//
// The bitmap cache from clusterStyle.js isn't reused here: macro features are
// at most one per registered backend (typically <20), so per-frame redraws
// cost <1 ms and the cache key complexity isn't worth it.

import Style from 'ol/style/Style.js';
import { hidpiStyleOptions } from '../lib/hidpiRenderer.js';
import { radiusForCount } from '../lib/clusterStyle.js';
import { COMPLETENESS_BASE, RING_CASING, FINDER } from '../lib/completenessPalette.js';

// Mapping-detail segments come from the shared palette (see
// lib/completenessPalette.js) so macro rings, cluster rings, polygons and the
// legend cannot drift apart.
//
// `restricted` is a different axis — access, not mapping detail. It has moved
// twice to stay clear of `missing`: originally grey, which collided when
// `missing` became grey; then slate-600, which collided again when `missing`
// moved to slate-500. Violet leaves the neutral range altogether and reads as
// "different kind of thing" rather than "even less mapped", which is right.
//
// Note what this colour actually paints, because an earlier version of this
// comment had it wrong. deriveMacroRing (hub/macroAggregate.js) sets
// restricted: 0 whenever a backend reports completeness, and routes the WHOLE
// count into restricted only when completeness is unknown — a pre-P1 backend.
// So `restricted` and `missing` never share a ring; the separation that
// matters is between two rings sitting side by side on the same macro view,
// not between adjacent arcs.
//
// A consequence worth stating: violet-600 paints the ENTIRE ring for those
// backends, so the ones we know least about are the loudest thing on the macro
// view. That was reviewed against the rendered map and kept deliberately. The
// macro view is largely an operator surface — it sits beside the InstancePanel
// health list — and a conspicuous ring over a backend that has not been
// upgraded is a useful "go fix that stack" signal rather than noise. Contrast
// is 5.21:1 against the basemap, the highest in the set.
//
// Note also that the slot is named `restricted` but, in the macro view, only
// ever carries "completeness unknown": deriveMacroRing zeroes it whenever a
// backend reports completeness, and the cluster ring renders no restricted
// segment at all. The name is historical; the colour has exactly one meaning
// on screen.
const COLOR = {
  ...COMPLETENESS_BASE,
  restricted: '#7c3aed', // tailwind violet-600 — also used for unknown-completeness rings
};
const RING_WIDTH      = 14; // slightly thicker than cluster (12) for country-scale prominence
const CENTER_FILL     = 'rgba(255, 255, 255, 0.95)';
const CENTER_STROKE   = '#1f2937';
const CENTER_TEXT     = '#1f2937';
// gray-400. Free of the mapping-detail palette again since `missing` moved to
// slate-500 — offline and no-match rings are the only plain greys left.
const OFFLINE_STROKE   = '#9ca3af';
const OFFLINE_FILL     = 'rgba(255, 255, 255, 0.85)';
const OFFLINE_TEXT     = '#6b7280';
const DEGRADED_STROKE  = '#d97706'; // amber-600
const DEGRADED_FILL    = 'rgba(255, 251, 235, 0.92)'; // amber-50
const DEGRADED_TEXT    = '#92400e'; // amber-800
const IMPORTING_STROKE = '#3b82f6'; // blue-500
const IMPORTING_FILL   = 'rgba(239, 246, 255, 0.92)'; // blue-50
const IMPORTING_TEXT   = '#1e40af'; // blue-800
const NOMATCH_STROKE   = '#9ca3af'; // gray-400 — filter excluded all playgrounds
const NOMATCH_FILL     = 'rgba(249, 250, 251, 0.92)'; // gray-50
const NOMATCH_TEXT     = '#6b7280'; // gray-500
const CANTFILTER_HALO  = '#6b7280'; // gray-500 — dashed "filter not applied" halo
const CANTFILTER_TEXT  = '#6b7280'; // gray-500
const COUNT_FONT      = 'bold 22px ui-monospace, "SF Mono", Menlo, system-ui, -apple-system, sans-serif';
const LABEL_FONT      = '600 11px system-ui, -apple-system, "Helvetica Neue", sans-serif';

// Each segment is rounded independently, then the residual is applied to the
// LARGEST segment, exactly as clusterStyle.quantise does.
//
// The residual used to be dumped into `restricted`, which invented data: with
// complete/partial/missing = 33/33/34 every segment rounds to 3, leaving a
// residual of 1 that painted a 36-degree "access restricted" wedge on a
// backend with no restricted playgrounds at all. Overshoot was clamped to 0
// without rescaling the rest, so 25/25/50 produced 3+3+5 = 11 tenths and the
// last arc wrapped past twelve o'clock over the start of the first.
export function quantiseSegments(complete, partial, missing, restricted) {
  const total = complete + partial + missing + restricted || 1;
  const seg = [complete, partial, missing, restricted]
    .map(v => Math.round((v / total) * 10));

  const residual = 10 - seg.reduce((a, b) => a + b, 0);
  if (residual !== 0) {
    let largest = 0;
    for (let i = 1; i < seg.length; i++) {
      if (seg[i] > seg[largest]) largest = i;
    }
    seg[largest] = Math.max(0, seg[largest] + residual);
  }
  return seg;
}

function renderHealthyMacroRing(pixelCoords, state) {
  const [x, y]     = pixelCoords;
  const f          = state.feature;
  const count      = f.get('count')      ?? 0;
  const complete   = f.get('complete')   ?? 0;
  const partial    = f.get('partial')    ?? 0;
  const missing    = f.get('missing')    ?? 0;
  const restricted = f.get('restricted') ?? 0;
  const ctx        = state.context;
  const radius     = radiusForCount(count);

  const tenths = quantiseSegments(complete, partial, missing, restricted);
  const colors = [COLOR.complete, COLOR.partial, COLOR.missing, COLOR.restricted];

  // Casing: one dark ring a pixel proud on each side, under the coloured
  // arcs. It carries the ring's edge against the basemap so the segment
  // colours are free to follow the ramp rather than a contrast ratio. One
  // full circle rather than per-arc, so adjoining segments share a casing and
  // show no seam. See RING_CASING in lib/completenessPalette.js.
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = RING_CASING;
  ctx.lineWidth   = RING_WIDTH + 2;
  ctx.stroke();

  ctx.lineWidth = RING_WIDTH;
  ctx.lineCap   = 'butt';
  let start = -Math.PI / 2; // 12 o'clock
  for (let i = 0; i < 4; i++) {
    if (tenths[i] === 0) continue;
    const end = start + (tenths[i] / 10) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, start, end);
    ctx.strokeStyle = colors[i];
    ctx.stroke();
    start = end;
  }

  // Inner disc + count
  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = CENTER_FILL;
  ctx.strokeStyle = CENTER_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle    = CENTER_TEXT;
  ctx.font         = COUNT_FONT;
  if ('fontFeatureSettings' in ctx) ctx.fontFeatureSettings = '"tnum" 1';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatCount(count), x, y + 0.5);
}

function renderOfflineMacroRing(pixelCoords, state) {
  const [x, y]   = pixelCoords;
  const f        = state.feature;
  const count    = f.get('count') ?? 0;
  const ctx      = state.context;
  const radius   = radiusForCount(count);

  // Dashed outline ring — no segment colours since the data is stale.
  ctx.lineWidth   = RING_WIDTH;
  ctx.lineCap     = 'butt';
  ctx.strokeStyle = OFFLINE_STROKE;
  ctx.setLineDash([8, 5]);
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Inner disc — keeps the count + label legible against the basemap.
  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = OFFLINE_FILL;
  ctx.strokeStyle = OFFLINE_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  // Last-known count, shifted up to make room for the "offline" label.
  ctx.fillStyle    = OFFLINE_TEXT;
  ctx.font         = COUNT_FONT;
  if ('fontFeatureSettings' in ctx) ctx.fontFeatureSettings = '"tnum" 1';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatCount(count), x, y - 7);

  ctx.font = LABEL_FONT;
  ctx.fillText('offline', x, y + 11);
}

function renderDegradedMacroRing(pixelCoords, state) {
  const [x, y] = pixelCoords;
  const ctx    = state.context;
  const radius = 26; // minimum — count is 0

  ctx.lineWidth   = RING_WIDTH;
  ctx.lineCap     = 'butt';
  ctx.strokeStyle = DEGRADED_STROKE;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = DEGRADED_FILL;
  ctx.strokeStyle = DEGRADED_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle    = DEGRADED_TEXT;
  ctx.font         = LABEL_FONT;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('no data', x, y);
}

// Filtered-empty: backend is healthy but the active filter matched zero of
// its playgrounds. Grey ring, "no match" label — distinct from the amber
// "no data" degraded ring (which means the backend itself is empty) and
// only ever shown while a filter is active.
function renderFilteredEmptyMacroRing(pixelCoords, state) {
  const [x, y] = pixelCoords;
  const ctx    = state.context;
  const radius = 26; // minimum — filtered count is 0

  ctx.lineWidth   = RING_WIDTH;
  ctx.lineCap     = 'butt';
  ctx.strokeStyle = NOMATCH_STROKE;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = NOMATCH_FILL;
  ctx.strokeStyle = NOMATCH_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle    = NOMATCH_TEXT;
  ctx.font         = LABEL_FONT;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('no match', x, y);
}

// Can't-filter: backend couldn't apply the active filter (no bbox, or it 404s
// the cluster RPC). We still show its full unfiltered catalogue (segments +
// count, like healthy) but mark it with a dashed grey halo + "unfiltered" label
// so the user knows this ring is NOT a filtered subset (#688).
function renderCantFilterMacroRing(pixelCoords, state) {
  const [x, y]     = pixelCoords;
  const f          = state.feature;
  const count      = f.get('count')      ?? 0;
  const complete   = f.get('complete')   ?? 0;
  const partial    = f.get('partial')    ?? 0;
  const missing    = f.get('missing')    ?? 0;
  const restricted = f.get('restricted') ?? 0;
  const ctx        = state.context;
  const radius     = radiusForCount(count);

  const tenths = quantiseSegments(complete, partial, missing, restricted);
  const colors = [COLOR.complete, COLOR.partial, COLOR.missing, COLOR.restricted];
  // Casing: one dark ring a pixel proud on each side, under the coloured
  // arcs. It carries the ring's edge against the basemap so the segment
  // colours are free to follow the ramp rather than a contrast ratio. One
  // full circle rather than per-arc, so adjoining segments share a casing and
  // show no seam. See RING_CASING in lib/completenessPalette.js.
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = RING_CASING;
  ctx.lineWidth   = RING_WIDTH + 2;
  ctx.stroke();

  ctx.lineWidth = RING_WIDTH;
  ctx.lineCap   = 'butt';
  let start = -Math.PI / 2;
  for (let i = 0; i < 4; i++) {
    if (tenths[i] === 0) continue;
    const end = start + (tenths[i] / 10) * Math.PI * 2;
    ctx.beginPath();
    ctx.arc(x, y, radius, start, end);
    ctx.strokeStyle = colors[i];
    ctx.stroke();
    start = end;
  }

  // Dashed grey halo just outside the ring — the orthogonal "filter not applied
  // here" marker (a colour-independent channel, distinct from offline's dashed
  // ring at the ring radius itself).
  ctx.lineWidth   = 2;
  ctx.strokeStyle = CANTFILTER_HALO;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.arc(x, y, radius + RING_WIDTH / 2 + 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  // Inner disc + count (shifted up) + "unfiltered" label.
  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = CENTER_FILL;
  ctx.strokeStyle = CENTER_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle    = CENTER_TEXT;
  ctx.font         = COUNT_FONT;
  if ('fontFeatureSettings' in ctx) ctx.fontFeatureSettings = '"tnum" 1';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatCount(count), x, y - 7);

  ctx.fillStyle = CANTFILTER_TEXT;
  ctx.font      = LABEL_FONT;
  ctx.fillText('unfiltered', x, y + 11);
}

function renderImportingMacroRing(pixelCoords, state) {
  const [x, y]   = pixelCoords;
  const f        = state.feature;
  const count    = f.get('count') ?? 0;
  const ctx      = state.context;
  const radius   = radiusForCount(count);

  ctx.lineWidth   = RING_WIDTH;
  ctx.lineCap     = 'butt';
  ctx.strokeStyle = IMPORTING_STROKE;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x, y, radius - RING_WIDTH / 2 - 1, 0, Math.PI * 2);
  ctx.fillStyle   = IMPORTING_FILL;
  ctx.strokeStyle = IMPORTING_STROKE;
  ctx.lineWidth   = 1;
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle    = IMPORTING_TEXT;
  ctx.font         = COUNT_FONT;
  if ('fontFeatureSettings' in ctx) ctx.fontFeatureSettings = '"tnum" 1';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(formatCount(count), x, y - 7);

  ctx.font = LABEL_FONT;
  ctx.fillText('updating', x, y + 11);
}

// Compact display: 65000 → "65k", 1500 → "1.5k", <1000 unchanged. Spec
// scenario uses 65000 directly but at radius 44 the digit string blows
// outside the inner disc; the "k" suffix keeps it inside.
function formatCount(n) {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}

// ── Finder variants (mapping detail off, #924) ──────────────────────────────
// A healthy backend becomes a plain emerald disc with its total; can't-filter
// keeps its dashed halo and label on top of that disc. The health variants
// (offline, importing, degraded, no match) are about the backend, not about
// mapping detail, and render the same in both modes.
function drawFinderMacroDisc(ctx, x, y, radius) {
  const r = radius + RING_WIDTH / 2;
  const trace = () => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); };
  trace(); ctx.strokeStyle = FINDER.casing; ctx.lineWidth = 5; ctx.stroke();
  trace(); ctx.strokeStyle = FINDER.border; ctx.lineWidth = 3; ctx.stroke();
  trace(); ctx.fillStyle   = FINDER.fill;   ctx.fill();
}

function drawFinderMacroCount(ctx, text, x, y) {
  ctx.font         = COUNT_FONT;
  if ('fontFeatureSettings' in ctx) ctx.fontFeatureSettings = '"tnum" 1';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin     = 'round';
  ctx.lineWidth    = 3;
  ctx.strokeStyle  = FINDER.casing;
  ctx.strokeText(text, x, y);
  ctx.fillStyle    = FINDER.text;
  ctx.fillText(text, x, y);
}

function renderFinderHealthyMacro(pixelCoords, state) {
  const [x, y] = pixelCoords;
  const count  = state.feature.get('count') ?? 0;
  drawFinderMacroDisc(state.context, x, y, radiusForCount(count));
  drawFinderMacroCount(state.context, formatCount(count), x, y + 0.5);
}

function renderFinderCantFilterMacro(pixelCoords, state) {
  const [x, y] = pixelCoords;
  const ctx    = state.context;
  const count  = state.feature.get('count') ?? 0;
  const radius = radiusForCount(count);
  drawFinderMacroDisc(ctx, x, y, radius);

  ctx.lineWidth   = 2;
  ctx.strokeStyle = CANTFILTER_HALO;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.arc(x, y, radius + RING_WIDTH / 2 + 4, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);

  drawFinderMacroCount(ctx, formatCount(count), x, y - 7);
  ctx.font      = LABEL_FONT;
  ctx.lineWidth = 3;
  ctx.strokeStyle = FINDER.casing;
  ctx.strokeText('unfiltered', x, y + 11);
  ctx.fillStyle = FINDER.text;
  ctx.fillText('unfiltered', x, y + 11);
}

const healthyStyle       = new Style(hidpiStyleOptions(renderHealthyMacroRing));
const offlineStyle       = new Style(hidpiStyleOptions(renderOfflineMacroRing));
const degradedStyle      = new Style(hidpiStyleOptions(renderDegradedMacroRing));
const importingStyle     = new Style(hidpiStyleOptions(renderImportingMacroRing));
const filteredEmptyStyle = new Style(hidpiStyleOptions(renderFilteredEmptyMacroRing));
const cantFilterStyle    = new Style(hidpiStyleOptions(renderCantFilterMacroRing));
const finderHealthyStyle    = new Style(hidpiStyleOptions(renderFinderHealthyMacro));
const finderCantFilterStyle = new Style(hidpiStyleOptions(renderFinderCantFilterMacro));

export function macroRingStyleFn(feature) {
  // Priority mirrors "most severe / least filtered dominates": backend-health
  // states first (offline, importing), then "can't filter" — a partial-coverage
  // condition that must outrank a normal/filtered ring so the user sees the
  // filter wasn't applied here (#688) — then the filter outcomes ("no match"
  // sits just above degraded; both signal an empty ring, but "no match" is a
  // filter result on a healthy backend, not missing data).
  if (feature.get('_offline'))       return offlineStyle;
  if (feature.get('_importing'))     return importingStyle;
  if (feature.get('_cantFilter'))    return cantFilterStyle;
  if (feature.get('_filteredEmpty')) return filteredEmptyStyle;
  if (feature.get('_degraded'))      return degradedStyle;
  return healthyStyle;
}

/** Macro style with mapping detail off: health variants as above, the rest neutral. */
export function finderMacroRingStyleFn(feature) {
  const style = macroRingStyleFn(feature);
  if (style === healthyStyle)    return finderHealthyStyle;
  if (style === cantFilterStyle) return finderCantFilterStyle;
  return style;
}
